import { useEffect, useMemo, useState } from 'react';
import { getReaderChapterVoice, ReaderChapter } from '@/services/booksApi';
import { resolveAssetUrl } from '@/utils/offlineAssets';
import {
  ChapterRuntime,
  parseCompactTimingMap,
  parseCompactTokenMap,
} from '@/utils/ordinalSync';

export type ChapterTimingsStatus = 'loading' | 'ready' | 'unavailable' | 'error';

export interface UseChapterAudioResult {
  runtime: ChapterRuntime | null;
  timingsStatus: ChapterTimingsStatus;
  error: string | null;
}

const RUNTIME_FETCH_TIMEOUT_MS = 15_000;
const RUNTIME_CACHE_LIMIT = 3;
const runtimeCache = new Map<string, ChapterRuntime>();
let pinnedRuntimeKey: string | null = null;
interface RuntimeRequest {
  controller: AbortController;
  promise: Promise<ChapterRuntime>;
  consumers: Set<symbol>;
}
const runtimeRequests = new Map<string, RuntimeRequest>();

const runtimeKey = (chapter: ReaderChapter, voiceKey: string): string => {
  const voice = getReaderChapterVoice(chapter, voiceKey);
  return `${chapter.tokenMapUrl ?? chapter.id}:${voice?.timingsUrl ?? voiceKey}`;
};

export const clearChapterRuntimeCache = (): void => {
  runtimeRequests.forEach((request) => request.controller.abort());
  runtimeCache.clear();
  runtimeRequests.clear();
  pinnedRuntimeKey = null;
};

const getCachedRuntime = (key: string): ChapterRuntime | undefined => {
  const runtime = runtimeCache.get(key);
  if (runtime) {
    runtimeCache.delete(key);
    runtimeCache.set(key, runtime);
  }
  return runtime;
};

const cacheRuntime = (key: string, runtime: ChapterRuntime): void => {
  runtimeCache.delete(key);
  runtimeCache.set(key, runtime);
  while (runtimeCache.size > RUNTIME_CACHE_LIMIT) {
    const evictionKey = Array.from(runtimeCache.keys()).find((candidate) => candidate !== pinnedRuntimeKey);
    if (!evictionKey) break;
    runtimeCache.delete(evictionKey);
  }
};

export const setChapterRuntimePinned = (
  chapter: ReaderChapter | null,
  voiceKey: string | null,
  pinned: boolean,
): void => {
  const key = chapter && voiceKey ? runtimeKey(chapter, voiceKey) : null;
  if (pinned && key) {
    pinnedRuntimeKey = key;
    getCachedRuntime(key);
  } else if (!key || pinnedRuntimeKey === key) {
    pinnedRuntimeKey = null;
  }
};

export const getChapterRuntimeCacheSize = (): number => runtimeCache.size;

const loadRuntimeUncached = async (
  chapter: ReaderChapter,
  voiceKey: string,
  externalSignal?: AbortSignal,
): Promise<ChapterRuntime> => {
  const voice = getReaderChapterVoice(chapter, voiceKey);
  if (!voice || !voice.wordSync.available || !chapter.tokenMapUrl) {
    throw new Error(`Chapter '${chapter.id}' has no synchronized runtime for voice '${voiceKey}'`);
  }

  const controller = new AbortController();
  const handleExternalAbort = () => controller.abort();
  if (externalSignal?.aborted) {
    controller.abort();
  }
  externalSignal?.addEventListener('abort', handleExternalAbort, { once: true });
  let didTimeout = false;
  const timeoutId = window.setTimeout(() => {
    didTimeout = true;
    controller.abort();
  }, RUNTIME_FETCH_TIMEOUT_MS);
  try {
    const [tokenResponse, timingResponse] = await Promise.all([
      fetch(resolveAssetUrl(chapter.tokenMapUrl), { signal: controller.signal }),
      fetch(resolveAssetUrl(voice.timingsUrl), { signal: controller.signal }),
    ]);
    if (!tokenResponse.ok) {
      throw new Error(`Failed to fetch token map: ${tokenResponse.statusText}`);
    }
    if (!timingResponse.ok) {
      throw new Error(`Failed to fetch timings: ${timingResponse.statusText}`);
    }

    const [rawTokenMap, rawTimings] = await Promise.all([
      tokenResponse.json() as Promise<unknown>,
      timingResponse.json() as Promise<unknown>,
    ]);
    const tokenMap = parseCompactTokenMap(rawTokenMap, chapter.id);
    const timings = parseCompactTimingMap(rawTimings, chapter.id, voiceKey);
    const expectedTokenCount = voice.wordSync.tokenCount;
    const expectedTimingCount = voice.wordSync.timingCount;
    if (
      tokenMap.tokens.length !== timings.startsMs.length ||
      tokenMap.tokens.length !== chapter.tokenCount ||
      tokenMap.tokens.length !== expectedTokenCount ||
      timings.startsMs.length !== expectedTimingCount
    ) {
      throw new Error(`Ordinal counts do not agree for chapter '${chapter.id}' and voice '${voiceKey}'`);
    }

    return { tokenMap, timings };
  } catch (error) {
    if (!controller.signal.aborted) {
      controller.abort();
    }
    if (didTimeout) {
      throw new Error(`Chapter runtime request timed out after ${RUNTIME_FETCH_TIMEOUT_MS / 1000} seconds`);
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
    externalSignal?.removeEventListener('abort', handleExternalAbort);
  }
};

export const fetchChapterRuntime = async (
  chapter: ReaderChapter,
  voiceKey: string,
  signal?: AbortSignal,
): Promise<ChapterRuntime> => {
  const key = runtimeKey(chapter, voiceKey);
  const cached = getCachedRuntime(key);
  if (cached) {
    return cached;
  }

  let pending = runtimeRequests.get(key);
  if (!pending) {
    const controller = new AbortController();
    const request = {
      controller,
      consumers: new Set(),
    } as RuntimeRequest;
    request.promise = loadRuntimeUncached(chapter, voiceKey, controller.signal).then((runtime) => {
      cacheRuntime(key, runtime);
      return runtime;
    }).finally(() => {
      if (runtimeRequests.get(key) === request) runtimeRequests.delete(key);
    });
    runtimeRequests.set(key, request);
    pending = request;
  }

  const consumer = Symbol(key);
  pending.consumers.add(consumer);
  const release = () => {
    pending?.consumers.delete(consumer);
    if (pending && pending.consumers.size === 0 && runtimeRequests.get(key) === pending) {
      pending.controller.abort();
    }
  };
  if (signal?.aborted) {
    release();
    throw new DOMException('aborted', 'AbortError');
  }
  return new Promise<ChapterRuntime>((resolve, reject) => {
    const handleAbort = () => {
      release();
      reject(new DOMException('aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', handleAbort, { once: true });
    pending!.promise.then(resolve, reject).finally(() => {
      signal?.removeEventListener('abort', handleAbort);
      release();
    });
  });
};

export const useChapterAudio = (
  chapter: ReaderChapter | null,
  voiceKey: string | null,
): UseChapterAudioResult => {
  const requestKey = useMemo(
    () => chapter && voiceKey ? runtimeKey(chapter, voiceKey) : null,
    [chapter, voiceKey],
  );
  const [state, setState] = useState<{
    key: string | null;
    runtime: ChapterRuntime | null;
    status: ChapterTimingsStatus;
    error: string | null;
  }>({ key: null, runtime: null, status: 'unavailable', error: null });

  useEffect(() => {
    const controller = new AbortController();
    if (!chapter || !voiceKey || chapter.contentMode !== 'standard') {
      setState({ key: null, runtime: null, status: 'unavailable', error: null });
      return () => controller.abort();
    }

    const voice = getReaderChapterVoice(chapter, voiceKey);
    if (!voice?.wordSync.available || !chapter.tokenMapUrl) {
      setState({ key: requestKey, runtime: null, status: 'unavailable', error: null });
      return () => controller.abort();
    }

    setState({ key: requestKey, runtime: null, status: 'loading', error: null });
    void fetchChapterRuntime(chapter, voiceKey, controller.signal)
      .then((runtime) => {
        if (!controller.signal.aborted) {
          setState({ key: requestKey, runtime, status: 'ready', error: null });
        }
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        const message = error instanceof Error ? error.message : 'Failed to load chapter synchronization';
        console.warn(`[reader] Synchronization unavailable for chapter '${chapter.id}'.`, error);
        setState({ key: requestKey, runtime: null, status: 'error', error: message });
      });

    return () => controller.abort();
  }, [chapter, requestKey, voiceKey]);

  if (state.key !== requestKey) {
    return { runtime: null, timingsStatus: requestKey ? 'loading' : 'unavailable', error: null };
  }
  return { runtime: state.runtime, timingsStatus: state.status, error: state.error };
};
